import { useState } from "react";
import { X, Home, ArrowDownCircle, ArrowUpCircle, Calendar, BarChart3, Settings, FileText, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

interface PalettePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  palette: {
    name: string;
    colors: Record<string, string>;
  };
}

// Mock data for preview
const mockTransactions = [
  {
    id: 1,
    description: "Venda de Produto",
    amount: 2500.00,
    type: "income",
    category: "Vendas",
    date: "2024-01-15",
    is_paid: true,
    status: "paid"
  },
  {
    id: 2,
    description: "Aluguel do Escritório",
    amount: 1500.00,
    type: "expense",
    category: "Aluguel",
    date: "2024-01-10",
    is_paid: false,
    status: "overdue"
  },
  {
    id: 3,
    description: "Consultoria",
    amount: 3200.00,
    type: "income",
    category: "Serviços",
    date: "2024-01-20",
    is_paid: false,
    status: "due-soon"
  },
  {
    id: 4,
    description: "Fornecedor XYZ",
    amount: 850.00,
    type: "expense",
    category: "Fornecedores",
    date: "2024-01-18",
    is_paid: true,
    status: "paid"
  }
];

const mockCategories = [
  { name: "Vendas", total: 12500, percentage: 45 },
  { name: "Serviços", total: 8300, percentage: 30 },
  { name: "Aluguel", total: 4500, percentage: 16 },
  { name: "Fornecedores", total: 2500, percentage: 9 }
];

const navigationItems = [
  { id: "dashboard", label: "Dashboard", icon: Home },
  { id: "transactions", label: "Transações", icon: FileText },
  { id: "incomes", label: "Receitas", icon: ArrowUpCircle },
  { id: "expenses", label: "Despesas", icon: ArrowDownCircle },
  { id: "calendar", label: "Calendário", icon: Calendar },
  { id: "reports", label: "Relatórios", icon: BarChart3 },
  { id: "settings", label: "Configurações", icon: Settings }
];

export function PalettePreviewModal({ isOpen, onClose, palette }: PalettePreviewModalProps) {
  const [activeView, setActiveView] = useState("dashboard");

  if (!isOpen) return null;

  // Apply palette colors to a scoped style
  const paletteStyle = Object.entries(palette.colors).reduce((acc, [key, value]) => {
    return `${acc}--preview-${key}: ${value};`;
  }, "");

  const renderDashboard = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-elevated" style={{ borderColor: `hsl(var(--preview-primary))` }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4" style={{ color: `hsl(var(--preview-primary))` }} />
              Saldo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: `hsl(var(--preview-primary))` }}>
              R$ 15.850,00
            </div>
            <p className="text-xs text-muted-foreground mt-1">Saldo atual</p>
          </CardContent>
        </Card>

        <Card className="card-elevated" style={{ borderColor: `hsl(var(--preview-success))` }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ArrowUpCircle className="h-4 w-4" style={{ color: `hsl(var(--preview-success))` }} />
              Receitas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: `hsl(var(--preview-success))` }}>
              R$ 20.800,00
            </div>
            <p className="text-xs text-muted-foreground mt-1">Mês atual</p>
          </CardContent>
        </Card>

        <Card className="card-elevated" style={{ borderColor: `hsl(var(--preview-error))` }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ArrowDownCircle className="h-4 w-4" style={{ color: `hsl(var(--preview-error))` }} />
              Despesas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: `hsl(var(--preview-error))` }}>
              R$ 8.950,00
            </div>
            <p className="text-xs text-muted-foreground mt-1">Mês atual</p>
          </CardContent>
        </Card>

        <Card className="card-elevated" style={{ borderColor: `hsl(var(--preview-warning))` }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="h-4 w-4" style={{ color: `hsl(var(--preview-warning))` }} />
              Pendentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: `hsl(var(--preview-warning))` }}>
              R$ 4.650,00
            </div>
            <p className="text-xs text-muted-foreground mt-1">A receber</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" style={{ color: `hsl(var(--preview-primary))` }} />
              Top Categorias
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {mockCategories.map((category, index) => (
                <div key={category.name} className="flex items-center justify-between p-3 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold text-white"
                      style={{ backgroundColor: `hsl(var(--preview-${index === 0 ? 'primary' : index === 1 ? 'accent' : index === 2 ? 'secondary' : 'success'}))` }}
                    >
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium">{category.name}</p>
                      <p className="text-sm text-muted-foreground">{category.percentage}% do total</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">
                      {category.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" style={{ color: `hsl(var(--preview-accent))` }} />
              Transações Recentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {mockTransactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted cursor-pointer transition-colors"
                >
                  <div className="flex-1">
                    <p className="font-medium text-sm">{transaction.description}</p>
                    <p className="text-xs text-muted-foreground">{transaction.category}</p>
                  </div>
                  <div className="text-right">
                    <p
                      className={`font-semibold text-sm`}
                      style={{
                        color: transaction.type === 'income'
                          ? `hsl(var(--preview-success))`
                          : `hsl(var(--preview-error))`
                      }}
                    >
                      {transaction.amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </p>
                    <Badge
                      variant={transaction.is_paid ? "default" : "secondary"}
                      className="text-xs"
                      style={transaction.is_paid ? {
                        backgroundColor: `hsl(var(--preview-success))`,
                        color: 'white'
                      } : {}}
                    >
                      {transaction.is_paid ? "Pago" : "Pendente"}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderTransactions = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Transações</h2>
        <Button style={{ backgroundColor: `hsl(var(--preview-primary))`, color: 'white' }}>
          Nova Transação
        </Button>
      </div>

      <Card className="card-elevated">
        <CardContent className="p-0">
          <div className="divide-y">
            <div className="grid grid-cols-12 gap-4 px-4 py-3 text-sm font-medium text-muted-foreground bg-muted">
              <div className="col-span-1">Status</div>
              <div className="col-span-2">Data</div>
              <div className="col-span-4">Descrição</div>
              <div className="col-span-2">Categoria</div>
              <div className="col-span-3 text-right">Valor</div>
            </div>
            {mockTransactions.map((transaction) => (
              <div
                key={transaction.id}
                className="grid grid-cols-12 gap-4 px-4 py-3 hover:bg-muted cursor-pointer transition-colors"
              >
                <div className="col-span-1">
                  <Badge
                    variant={transaction.is_paid ? "default" : "secondary"}
                    className="text-xs"
                    style={transaction.is_paid ? {
                      backgroundColor: `hsl(var(--preview-success))`,
                      color: 'white'
                    } : transaction.status === 'overdue' ? {
                      backgroundColor: `hsl(var(--preview-error))`,
                      color: 'white'
                    } : {}}
                  >
                    {transaction.is_paid ? "Pago" : transaction.status === 'overdue' ? "Vencido" : "Pendente"}
                  </Badge>
                </div>
                <div className="col-span-2 text-sm">{transaction.date}</div>
                <div className="col-span-4 font-medium text-sm">{transaction.description}</div>
                <div className="col-span-2">
                  <Badge variant="outline" className="text-xs">{transaction.category}</Badge>
                </div>
                <div
                  className="col-span-3 text-right font-semibold text-sm"
                  style={{
                    color: transaction.type === 'income'
                      ? `hsl(var(--preview-success))`
                      : `hsl(var(--preview-error))`
                  }}
                >
                  {transaction.amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderView = () => {
    switch (activeView) {
      case "transactions":
      case "incomes":
      case "expenses":
        return renderTransactions();
      case "dashboard":
      default:
        return renderDashboard();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-[95vw] h-[95vh] bg-background rounded-lg shadow-2xl flex flex-col overflow-hidden"
        style={{
          ...Object.fromEntries(
            Object.entries(palette.colors).map(([key, value]) => [`--preview-${key}`, value])
          )
        } as React.CSSProperties}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ backgroundColor: `hsl(var(--preview-sidebar))` }}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
              style={{ backgroundColor: `hsl(var(--preview-primary))` }}
            >
              CA
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Preview: {palette.name}</h1>
              <p className="text-xs text-muted-foreground">Navegação completa do sistema</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-card-foreground hover:bg-card/10"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <div
            className="w-64 border-r flex flex-col"
            style={{ backgroundColor: `hsl(var(--preview-sidebar))` }}
          >
            <ScrollArea className="flex-1">
              <nav className="p-4 space-y-1">
                {navigationItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeView === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveView(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                        isActive
                          ? 'bg-card/10 text-card-foreground font-medium'
                          : 'text-muted-foreground hover:bg-card/5 hover:text-card-foreground'
                      }`}
                      style={isActive ? {
                        backgroundColor: `hsl(var(--preview-primary))`,
                        color: 'white'
                      } : {}}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="text-sm">{item.label}</span>
                    </button>
                  );
                })}
              </nav>
            </ScrollArea>

            <div className="p-4 border-t border-border">
              <div className="flex items-center gap-3 text-muted-foreground">
                <div className="w-8 h-8 rounded-full bg-card/10 flex items-center justify-center">
                  <span className="text-xs font-medium">U</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">Usuário Demo</p>
                  <p className="text-xs text-muted-foreground truncate">demo@example.com</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-auto">
            <div className="p-6">
              {renderView()}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-3 bg-muted flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Preview em tempo real da paleta <strong>{palette.name}</strong>
          </p>
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              {Object.entries(palette.colors).slice(0, 7).map(([key, value]) => (
                <div
                  key={key}
                  className="w-6 h-6 rounded border border-border"
                  style={{ backgroundColor: `hsl(${value})` }}
                  title={key}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
